"use client";

import type { Recipe, RecipeBox } from "@/lib/types";
import jsPDF from "jspdf";

/**
 * Export a single recipe to PDF
 */
export async function exportRecipeToPDF(
  recipe: Recipe,
  recipeBox: RecipeBox | null,
  includeAds: boolean = false,
): Promise<void> {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let yPosition = margin;

  // Helper to add a new page if needed
  const checkPageBreak = (requiredHeight: number) => {
    if (yPosition + requiredHeight > pageHeight - margin) {
      doc.addPage();
      yPosition = margin;
    }
  };

  // Title
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  const titleLines = doc.splitTextToSize(recipe.title, contentWidth);
  doc.text(titleLines, margin, yPosition);
  yPosition += titleLines.length * 8 + 10;

  // Recipe Box name (if available)
  if (recipeBox) {
    checkPageBreak(10);
    doc.setFontSize(12);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(100, 100, 100);
    doc.text(`From: ${recipeBox.title}`, margin, yPosition);
    yPosition += 10;
    doc.setTextColor(0, 0, 0);
  }

  // Labels
  if (recipe.labels.length > 0) {
    checkPageBreak(10);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Labels: ${recipe.labels.join(", ")}`, margin, yPosition);
    yPosition += 10;
  }

  // Metadata (prep time, cook time, servings)
  checkPageBreak(15);
  doc.setFontSize(10);
  const metadata: string[] = [];
  if (recipe.prepTime) metadata.push(`Prep: ${recipe.prepTime}`);
  if (recipe.cookTime) metadata.push(`Cook: ${recipe.cookTime}`);
  if (recipe.servings) metadata.push(`Serves: ${recipe.servings}`);
  if (metadata.length > 0) {
    doc.text(metadata.join(" • "), margin, yPosition);
    yPosition += 10;
  }

  yPosition += 5;

  // Ingredients
  checkPageBreak(20);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Ingredients", margin, yPosition);
  yPosition += 10;

  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  recipe.ingredients.forEach((ingredient) => {
    checkPageBreak(8);
    doc.text(`• ${ingredient}`, margin + 5, yPosition);
    yPosition += 7;
  });

  yPosition += 5;

  // Directions
  checkPageBreak(20);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Directions", margin, yPosition);
  yPosition += 10;

  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  const directionsLines = doc.splitTextToSize(recipe.directions, contentWidth - 10);
  directionsLines.forEach((line: string) => {
    checkPageBreak(8);
    doc.text(line, margin + 5, yPosition);
    yPosition += 7;
  });

  // Notes
  if (recipe.notes) {
    yPosition += 5;
    checkPageBreak(20);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Notes", margin, yPosition);
    yPosition += 10;

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    const notesLines = doc.splitTextToSize(recipe.notes, contentWidth - 10);
    notesLines.forEach((line: string) => {
      checkPageBreak(8);
      doc.text(line, margin + 5, yPosition);
      yPosition += 7;
    });
  }

  // Add ads for free tier users
  if (includeAds) {
    doc.addPage();
    yPosition = margin;
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Upgrade to Pro", margin, yPosition);
    yPosition += 10;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const adText = "Remove ads and unlock unlimited recipes with ezRecipeBox Pro!";
    const adLines = doc.splitTextToSize(adText, contentWidth);
    adLines.forEach((line: string) => {
      doc.text(line, margin, yPosition);
      yPosition += 7;
    });
  }

  // Footer - Add branding to last page
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text(
    "ezRecipeBox",
    pageWidth - margin,
    pageHeight - 10,
    { align: "right" },
  );
  doc.setTextColor(0, 0, 0);

  // Save PDF
  doc.save(`${recipe.title.replace(/[^a-z0-9]/gi, "_")}.pdf`);
}

/**
 * Export recipe to JSON format
 */
export function exportRecipeToJSON(recipe: Recipe, recipeBox: RecipeBox | null): void {
  const exportData = {
    recipe: {
      title: recipe.title,
      labels: recipe.labels,
      ingredients: recipe.ingredients,
      directions: recipe.directions,
      notes: recipe.notes,
      prepTime: recipe.prepTime,
      cookTime: recipe.cookTime,
      servings: recipe.servings,
      imageUrl: recipe.imageUrl,
      imageHint: recipe.imageHint,
    },
    recipeBox: recipeBox
      ? {
          title: recipeBox.title,
          description: recipeBox.description,
        }
      : null,
    exportedAt: new Date().toISOString(),
    exportedFrom: "ezRecipeBox",
  };

  const jsonString = JSON.stringify(exportData, null, 2);
  const blob = new Blob([jsonString], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${recipe.title.replace(/[^a-z0-9]/gi, "_")}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export recipe to CSV format
 */
export function exportRecipeToCSV(recipe: Recipe, recipeBox: RecipeBox | null): void {
  const rows: string[][] = [
    ["Field", "Value"],
    ["Title", recipe.title],
    ["Recipe Box", recipeBox?.title || ""],
    ["Labels", recipe.labels.join("; ")],
    ["Prep Time", recipe.prepTime || ""],
    ["Cook Time", recipe.cookTime || ""],
    ["Servings", recipe.servings || ""],
    ["Ingredients", recipe.ingredients.join("; ")],
    ["Directions", recipe.directions.replace(/\n/g, " ")],
    ["Notes", recipe.notes?.replace(/\n/g, " ") || ""],
    ["Image URL", recipe.imageUrl || ""],
    ["Exported At", new Date().toISOString()],
  ];

  const csvContent = rows.map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(",")).join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${recipe.title.replace(/[^a-z0-9]/gi, "_")}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export recipe to RecipeML format (XML-based recipe format)
 */
export function exportRecipeToRecipeML(recipe: Recipe, recipeBox: RecipeBox | null): void {
  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<recipeml version="0.5">
  <recipe>
    <head>
      <title>${escapeXml(recipe.title)}</title>
      ${recipeBox ? `<categories><cat>${escapeXml(recipeBox.title)}</cat></categories>` : ""}
      ${recipe.labels.length > 0 ? `<categories>${recipe.labels.map((tag) => `<cat>${escapeXml(tag)}</cat>`).join("")}</categories>` : ""}
    </head>
    <ingredients>
      ${recipe.ingredients.map((ing) => `<ing><item>${escapeXml(ing)}</item></ing>`).join("\n      ")}
    </ingredients>
    <directions>
      <step>${escapeXml(recipe.directions)}</step>
    </directions>
    ${recipe.notes ? `<yield><yields>${escapeXml(recipe.notes)}</yields></yield>` : ""}
    ${recipe.prepTime ? `<preptime><time><qty>${escapeXml(recipe.prepTime)}</qty></time></preptime>` : ""}
    ${recipe.cookTime ? `<cooktime><time><qty>${escapeXml(recipe.cookTime)}</qty></time></cooktime>` : ""}
    ${recipe.servings ? `<yield><yields>${escapeXml(recipe.servings)}</yields></yield>` : ""}
  </recipe>
</recipeml>`;

  const blob = new Blob([xml], { type: "application/xml" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${recipe.title.replace(/[^a-z0-9]/gi, "_")}.xml`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export recipe to Markdown format
 */
export function exportRecipeToMarkdown(recipe: Recipe, recipeBox: RecipeBox | null): void {
  let markdown = `# ${recipe.title}\n\n`;

  if (recipeBox) {
    markdown += `**From:** ${recipeBox.title}\n\n`;
  }

  if (recipe.labels.length > 0) {
    markdown += `**Labels:** ${recipe.labels.map((tag) => `\`${tag}\``).join(", ")}\n\n`;
  }

  const metadata: string[] = [];
  if (recipe.prepTime) metadata.push(`**Prep Time:** ${recipe.prepTime}`);
  if (recipe.cookTime) metadata.push(`**Cook Time:** ${recipe.cookTime}`);
  if (recipe.servings) metadata.push(`**Servings:** ${recipe.servings}`);
  if (metadata.length > 0) {
    markdown += metadata.join(" | ") + "\n\n";
  }

  markdown += `## Ingredients\n\n`;
  recipe.ingredients.forEach((ingredient) => {
    markdown += `- ${ingredient}\n`;
  });

  markdown += `\n## Directions\n\n`;
  const directionsLines = recipe.directions.split(/\r?\n/).filter(Boolean);
  directionsLines.forEach((line, index) => {
    markdown += `${index + 1}. ${line}\n`;
  });

  if (recipe.notes) {
    markdown += `\n## Notes\n\n${recipe.notes}\n`;
  }

  markdown += `\n---\n*Exported from ezRecipeBox on ${new Date().toLocaleDateString()}*\n`;

  const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${recipe.title.replace(/[^a-z0-9]/gi, "_")}.md`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export recipe to plain text format
 */
export function exportRecipeToPlainText(recipe: Recipe, recipeBox: RecipeBox | null): void {
  let text = `${recipe.title}\n`;
  text += "=".repeat(recipe.title.length) + "\n\n";

  if (recipeBox) {
    text += `From: ${recipeBox.title}\n\n`;
  }

  if (recipe.labels.length > 0) {
    text += `Labels: ${recipe.labels.join(", ")}\n\n`;
  }

  const metadata: string[] = [];
  if (recipe.prepTime) metadata.push(`Prep Time: ${recipe.prepTime}`);
  if (recipe.cookTime) metadata.push(`Cook Time: ${recipe.cookTime}`);
  if (recipe.servings) metadata.push(`Servings: ${recipe.servings}`);
  if (metadata.length > 0) {
    text += metadata.join(" | ") + "\n\n";
  }

  text += "INGREDIENTS\n";
  text += "-".repeat(50) + "\n";
  recipe.ingredients.forEach((ingredient) => {
    text += `  • ${ingredient}\n`;
  });

  text += "\nDIRECTIONS\n";
  text += "-".repeat(50) + "\n";
  const directionsLines = recipe.directions.split(/\r?\n/).filter(Boolean);
  directionsLines.forEach((line, index) => {
    text += `${index + 1}. ${line}\n`;
  });

  if (recipe.notes) {
    text += "\nNOTES\n";
    text += "-".repeat(50) + "\n";
    text += recipe.notes + "\n";
  }

  text += `\n\nExported from ezRecipeBox on ${new Date().toLocaleDateString()}\n`;

  const blob = new Blob([text], { type: "text/plain;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${recipe.title.replace(/[^a-z0-9]/gi, "_")}.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export multiple recipes to JSON (bulk export)
 */
export function exportRecipesToJSON(recipes: Recipe[], recipeBox: RecipeBox | null): void {
  const exportData = {
    recipeBox: recipeBox
      ? {
          title: recipeBox.title,
          description: recipeBox.description,
        }
      : null,
    recipes: recipes.map((recipe) => ({
      title: recipe.title,
      labels: recipe.labels,
      ingredients: recipe.ingredients,
      directions: recipe.directions,
      notes: recipe.notes,
      prepTime: recipe.prepTime,
      cookTime: recipe.cookTime,
      servings: recipe.servings,
      imageUrl: recipe.imageUrl,
      imageHint: recipe.imageHint,
    })),
    exportedAt: new Date().toISOString(),
    exportedFrom: "ezRecipeBox",
    totalRecipes: recipes.length,
  };

  const jsonString = JSON.stringify(exportData, null, 2);
  const blob = new Blob([jsonString], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${recipeBox?.title.replace(/[^a-z0-9]/gi, "_") || "recipes"}_export.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export multiple recipes to CSV (bulk export)
 */
export function exportRecipesToCSV(recipes: Recipe[], recipeBox: RecipeBox | null): void {
  const headers = ["Title", "Labels", "Prep Time", "Cook Time", "Servings", "Ingredients", "Directions", "Notes"];
  const rows = recipes.map((recipe) => [
    recipe.title,
    recipe.labels.join("; "),
    recipe.prepTime || "",
    recipe.cookTime || "",
    recipe.servings || "",
    recipe.ingredients.join("; "),
    recipe.directions.replace(/\n/g, " "),
    recipe.notes?.replace(/\n/g, " ") || "",
  ]);

  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    .join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${recipeBox?.title.replace(/[^a-z0-9]/gi, "_") || "recipes"}_export.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Helper function to escape XML special characters
 */
function escapeXml(unsafe: string): string {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

